import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, TriangleAlert, FileText, MessageSquare,
  LayoutDashboard, Columns3, CheckCircle2, Clock, ListChecks, ShieldCheck, Settings, Menu, X,
} from "lucide-react";
import { MovaraMark } from "@/components/Brand";

function Nav() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 text-brand shrink-0">
          <MovaraMark className="h-6 w-7" />
          <span className="font-heading font-semibold text-[16px] sm:text-[17px] text-ink">MovaRaBoard</span>
        </Link>
        <div className="flex items-center gap-3 lg:gap-5 shrink-0">
          <Link to="/sample-board" data-testid="nav-sample-board" className="hidden md:inline text-[13.5px] text-ink-2 hover:text-ink transition-colors">View Sample Board</Link>
          <Link
            to="/request-access"
            data-testid="nav-walkthrough"
            className="hidden md:flex h-9 px-4 rounded-sm bg-[#D97706] text-white text-[13.5px] font-semibold items-center gap-1.5 hover:bg-[#B45309] transition-colors whitespace-nowrap"
          >
            Request a Visibility Walkthrough
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            className="md:hidden h-9 w-9 inline-flex items-center justify-center rounded-[5px] border border-line bg-surface text-ink hover:bg-surface-alt transition-colors"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-line bg-surface">
          <nav className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex flex-col">
            <Link to="/sample-board" onClick={close} data-testid="mobile-link-sample" className="py-3 text-[14px] text-ink border-b border-line">View Sample Board</Link>
            <Link
              to="/request-access"
              onClick={close}
              data-testid="mobile-link-walkthrough"
              className="my-3 h-11 rounded-sm bg-[#D97706] text-white text-[14px] font-semibold flex items-center justify-center gap-2 hover:bg-[#B45309] transition-colors"
            >
              Request a Visibility Walkthrough <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

/* ---------- Hero product preview: a live Needs-Attention table ---------- */
function PreviewRow({ job, customer, issue, risk, color, bg }) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-2 border-b border-line last:border-0">
      <span className="font-mono-data text-[10px] text-ink-2 w-14 shrink-0">{job}</span>
      <span className="text-[11px] text-ink truncate flex-1">{customer}</span>
      <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] font-medium whitespace-nowrap hidden sm:inline" style={{ color, backgroundColor: bg }}>{issue}</span>
      <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] font-medium whitespace-nowrap" style={{ color, backgroundColor: bg }}>{risk}</span>
    </div>
  );
}

function HeroPreview() {
  return (
    <div className="rounded-sm border-2 border-[#0B132B] bg-surface shadow-[4px_4px_0px_0px_#D97706] overflow-hidden">
      <div className="h-9 bg-sidebar flex items-center gap-2 px-3.5">
        <MovaraMark className="h-3.5 w-4 text-white" />
        <span className="text-white text-[11px] font-medium">MovaRaBoard · Hot Shipments</span>
        <div className="ml-auto flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
          <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
          <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
        </div>
      </div>
      <div className="p-3.5">
        <div className="bg-surface border border-line rounded-[5px] overflow-hidden">
          <div className="flex items-center justify-between px-2.5 py-2 border-b border-line bg-surface-alt">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-2 flex items-center gap-1.5">
              <TriangleAlert className="h-3 w-3 text-warn" /> Jobs that need action now
            </span>
            <span className="font-mono-data text-[10px] text-ink-3">15 flagged</span>
          </div>
          <PreviewRow job="MOV-1006" customer="Ironwood Building Materials" issue="Missing BOL" risk="Delayed" color="#DC2626" bg="#FEF2F2" />
          <PreviewRow job="MOV-1013" customer="Vantage Medical Supply" issue="Update overdue" risk="Customer Update Due" color="#D97706" bg="#FFFBEB" />
          <PreviewRow job="MOV-1002" customer="Cedarline Manufacturing" issue="No update 24h+" risk="Needs Attention" color="#D97706" bg="#FFFBEB" />
          <PreviewRow job="MOV-1019" customer="Solstice Beverage Co." issue="POD missing" risk="Delayed" color="#DC2626" bg="#FEF2F2" />
        </div>
        <p className="text-[10.5px] text-ink-3 mt-2.5 px-0.5">Every stuck job — with the reason, the owner, and the next action.</p>
      </div>
    </div>
  );
}

/* ---------- Section 2: The Problem ---------- */
const PROBLEMS = [
  ["01", "Status lives in too many places", "Shipment state is scattered across inboxes, spreadsheets, phone calls, and chat threads."],
  ["02", "Delays surface too late", "Problems show up after the customer has already called to ask where their freight is."],
  ["03", "Missing documents stall jobs", "A missing BOL or POD freezes a job at the worst possible moment in the lifecycle."],
  ["04", "Handoffs lose context", "Work passes between team members and the why, the next step, and the history get dropped."],
  ["05", "Customer updates are inconsistent", "Clients chase updates because there's no clear view of who was told what, and when."],
  ["06", "Managers lack a daily operating view", "There's no single place to answer: what needs attention today, and who's on it?"],
];

function Problem() {
  return (
    <section id="problem" className="scroll-mt-20 border-y border-line bg-surface">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-16">
        <p className="op-label text-steel mb-3">The daily visibility problem</p>
        <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold text-ink max-w-2xl leading-tight">
          Most teams do not lack effort. They lack one place to see the work.
        </h2>
        <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROBLEMS.map(([num, title, desc]) => (
            <div key={num} data-testid={`problem-card-${num}`} className="bg-surface border border-slate-200 rounded-sm p-5 sm:p-6 hover:border-[#0B132B] hover:shadow-[2px_2px_0px_0px_#0B132B] transition-all">
              <span className="text-lg font-mono font-extrabold text-slate-400">{num}</span>
              <h3 className="mt-2.5 font-heading text-[15px] font-semibold text-ink leading-snug">{title}</h3>
              <p className="mt-2 text-[13.5px] text-ink-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Section 3: Dashboard (the product view) ---------- */
const DASH_SCREENS = [
  { icon: LayoutDashboard, label: "Today" },
  { icon: ListChecks, label: "My Queue" },
  { icon: Columns3, label: "Dispatch Board" },
  { icon: TriangleAlert, label: "Hot Shipments" },
  { icon: FileText, label: "Manifests & PODs" },
  { icon: MessageSquare, label: "Broker Check-Ins" },
];

const DASH_TILES = [
  ["Active Jobs", "26", "#0B132B"],
  ["Hot Shipments", "15", "#D97706"],
  ["Missing Documents", "6", "#D97706"],
  ["Customer Updates Due", "8", "#D97706"],
  ["Ready to Close", "10", "#059669"],
  ["Closed", "42", "#64748B"],
];

function DashboardMock() {
  return (
    <div className="rounded-sm border-2 border-[#0B132B] bg-surface shadow-industrialCard overflow-hidden">
      <div className="h-9 bg-sidebar flex items-center gap-2 px-3.5">
        <MovaraMark className="h-3.5 w-4 text-white" />
        <span className="text-white text-[11px] font-medium">MovaRaBoard · Today</span>
      </div>
      <div className="flex">
        {/* mini sidebar */}
        <div className="w-36 shrink-0 bg-sidebar/95 hidden sm:block py-3 px-2.5 space-y-0.5">
          {DASH_SCREENS.map(({ icon: Icon, label }, i) => (
            <div key={label} className={`flex items-center gap-2 px-2 py-1.5 rounded-[4px] text-[11px] ${i === 0 ? "bg-white/[0.12] text-white" : "text-white/55"}`}>
              <Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {label}
            </div>
          ))}
        </div>
        {/* main */}
        <div className="flex-1 p-3.5 min-w-0">
          <div className="grid grid-cols-3 gap-2 mb-3">
            {DASH_TILES.map(([label, value, accent]) => (
              <div key={label} className="bg-surface border border-line rounded-[5px] p-2.5">
                <div className="h-1 w-6 rounded-full mb-2" style={{ backgroundColor: accent }} />
                <div className="font-mono-data text-base font-semibold text-ink leading-none">{value}</div>
                <div className="text-[8.5px] uppercase tracking-wider text-ink-3 font-semibold mt-1.5 leading-tight">{label}</div>
              </div>
            ))}
          </div>
          {/* lifecycle strip */}
          <div className="bg-surface border border-line rounded-[5px] px-3 py-2.5">
            <div className="text-[9px] uppercase tracking-wider text-ink-3 font-semibold mb-2">Closeout lifecycle</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] px-2 py-0.5 rounded-[3px] font-medium" style={{ color: "#0B132B", backgroundColor: "#E7ECF1" }}>Delivered</span>
              <ArrowRight className="h-3 w-3 text-ink-3" />
              <span className="text-[10px] px-2 py-0.5 rounded-[3px] font-medium" style={{ color: "#059669", backgroundColor: "#ECFDF5" }}>Ready to Close</span>
              <ArrowRight className="h-3 w-3 text-ink-3" />
              <span className="text-[10px] px-2 py-0.5 rounded-[3px] font-medium" style={{ color: "#64748B", backgroundColor: "#F1F5F9" }}>Closed</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  return (
    <section id="sample-board" className="scroll-mt-20 border-b border-line bg-app-bg">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-16 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
        <div className="order-2 lg:order-1"><DashboardMock /></div>
        <div className="order-1 lg:order-2">
          <p className="op-label text-steel mb-3">Try it with demo data</p>
          <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold text-ink leading-tight">
            Explore the Sample Board.
          </h2>
          <p className="mt-4 text-[14.5px] text-ink-2 leading-relaxed max-w-lg">
            See how MovaRaBoard organizes active jobs, blockers, missing documents, customer updates,
            and closeout using demo shipment data — Today, Dispatch Board, Hot Shipments, Manifests &amp; PODs,
            and Broker Check-Ins, all in one place.
          </p>
          <p className="mt-3 text-[13px] text-ink-3 leading-relaxed max-w-lg">
            It's a demo workspace with sample data, not a real client workspace — explore freely, no sign-in required.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2.5">
            {["Today dashboard", "Dispatch Board", "Hot Shipments", "My Queue", "Broker Check-Ins", "Ready to Close / Closed"].map((s) => (
              <li key={s} className="flex items-center gap-2 text-[13.5px] text-ink">
                <CheckCircle2 className="h-4 w-4 text-ok shrink-0" /> {s}
              </li>
            ))}
          </ul>
          <div className="mt-7">
            <Link
              to="/sample-board"
              data-testid="section-sample-btn"
              className="h-11 px-5 rounded-[5px] bg-brand text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-brand-dark transition-colors"
            >
              View Sample Board <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Section 4: Features ---------- */
const FEATURES = [
  { icon: LayoutDashboard, label: "Today View", desc: "See what needs attention before the day gets away from the team." },
  { icon: Columns3, label: "Dispatch Board", desc: "Track each job by stage, from new job to closed." },
  { icon: TriangleAlert, label: "Hot Shipments", desc: "See which jobs are blocked, delayed, overdue, or incomplete — all in one place." },
  { icon: ListChecks, label: "My Queue", desc: "Give each operator a personal list of assigned work and blockers." },
  { icon: FileText, label: "Manifests & PODs", desc: "Track missing, received, pending, and uploaded documents per job." },
  { icon: MessageSquare, label: "Broker Check-Ins", desc: "See who was updated, who is overdue, and what message should go out next." },
  { icon: ShieldCheck, label: "Closeout Guardrails", desc: "Move jobs Delivered → Ready to Close → Closed with clear blocker logic." },
  { icon: Settings, label: "Settings", desc: "Configure users, roles, document defaults, templates, and operational rules." },
];

function Features() {
  return (
    <section id="features" className="scroll-mt-20 border-b border-line bg-surface">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-16">
        <p className="op-label text-steel mb-3">Core capabilities</p>
        <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold text-ink mb-9">
          Built around the work that keeps shipments moving.
        </h2>
        <div className="grid sm:grid-cols-2 gap-px bg-line border border-line rounded-md overflow-hidden">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.label} data-testid={`feature-${f.label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-surface p-5 sm:p-6 flex gap-4 hover:bg-surface-alt transition-colors">
                <div className="h-10 w-10 shrink-0 rounded-[5px] bg-brand/[0.06] border border-line flex items-center justify-center">
                  <Icon className="h-5 w-5 text-brand" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-[15.5px] font-medium text-ink">{f.label}</h3>
                  <p className="mt-1.5 text-[13.5px] text-ink-2 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- Section 5: Final CTA ---------- */
function FinalCTA() {
  return (
    <section className="bg-sidebar text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-20 text-center">
        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-medium leading-tight max-w-2xl mx-auto">
          Give your team one clear place to see shipment work.
        </h2>
        <p className="mt-4 text-white/60 text-[14.5px] leading-relaxed max-w-2xl mx-auto">
          Start with a visibility walkthrough and see how MovaRaBoard can organize active jobs,
          blockers, documents, updates, and next actions.
        </p>
        <div className="mt-8 flex justify-center">
          <Link
            to="/request-access"
            data-testid="final-cta-btn"
            className="h-11 px-6 rounded-[5px] bg-white text-brand text-sm font-medium flex items-center justify-center gap-2 hover:bg-white/90 transition-colors"
          >
            Request a Visibility Walkthrough <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="bg-app-bg min-h-screen">
      <Nav />
      {/* Section 1: Hero */}
      <section className="bg-terminal">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div className="animate-fade-up">
            <p className="op-label mb-4 text-warn">Shipment visibility for logistics teams</p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.1] tracking-tight">
              See what's moving, what's stuck, and what needs action next.
            </h1>
            <p className="mt-5 text-[15px] sm:text-[16px] text-white/70 leading-relaxed max-w-xl">
              MovaRaBoard gives small and mid-sized logistics teams one clear board for
              shipment jobs, delays, missing documents, customer updates, and team handoffs.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to="/request-access"
                data-testid="hero-walkthrough-btn"
                className="h-11 px-5 rounded-sm bg-[#D97706] hover:bg-[#B45309] text-white text-sm font-semibold flex items-center justify-center gap-2 border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] active:translate-y-0.5 active:translate-x-0.5 active:shadow-none transition-all"
              >
                Request a Visibility Walkthrough <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/sample-board"
                data-testid="hero-sample-btn"
                className="h-11 px-5 rounded-sm border border-white/30 bg-transparent text-white text-sm font-semibold flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                View Sample Board
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-white/55">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-ok" /> No spreadsheets</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-warn" /> Set up in a day</span>
            </div>
          </div>
          <div className="animate-fade-up" style={{ animationDelay: "120ms" }}>
            <HeroPreview />
          </div>
        </div>
      </section>

      <Problem />
      <Dashboard />
      <Features />
      <FinalCTA />
      <footer className="border-t border-line bg-app-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-ink-2 text-sm">
            <MovaraMark className="h-4 w-5 text-brand" /> MovaRaBoard
          </div>
          <nav className="flex items-center gap-5 text-[13px] font-medium">
            <Link data-testid="footer-terms-link" to="/terms" className="text-slate-500 hover:text-[#0B132B] transition-colors">Terms &amp; Conditions</Link>
            <Link data-testid="footer-privacy-link" to="/privacy" className="text-slate-500 hover:text-[#0B132B] transition-colors">Privacy Policy</Link>
            <a data-testid="footer-support-link" href="mailto:support@movaraboard.com" className="text-slate-500 hover:text-[#0B132B] transition-colors">Contact Support</a>
          </nav>
          <p data-testid="landing-version" className="text-[12.5px] text-ink-3 font-mono-data">© 2026 MovaRaBoard · Shipment Visibility Platform</p>
        </div>
      </footer>
    </div>
  );
}
